// Video creator (Director de Estudio / asistente) — migrado de n8n
// (`webhook/generar_video_creator`) al atlas-backend `/v1/videos/creator`
// SOLO para ref_mode 'none' | 'reference' | 'frames' | 'dialogo' (ADR-584).
// `dialogo_movimiento` y `ugc` NO están migrados: siguen a n8n SIEMPRE,
// incluso con el flag activo (ruteo parcial por ref_mode acá adentro).
//
// Contrato de éxito idéntico en ambos: `{response:'success', path}` con el
// path relativo a `video-project-assets` (n8n legacy puede devolver
// `video_id` de imagenes_generadas en su lugar — el caller decide).
//
// Clasificación de errores (importa para la carrera del asistente):
//  • `GenerateAdError` = fallo DEFINITIVO del backend (402 billing, 500
//    tipado, respuesta de error explícita) → no hay video que esperar.
//  • `Error` plano = fallo TÉCNICO (timeout / red / 202 wait vencido) → el
//    video puede haberse generado igual; un poll puede recuperarlo.

import { atlasBackendFetch, pollJobUntilSettled } from './atlasBackend';
import { N8N_ONLY_REF_MODES } from './creatorRetry';
import {
  GENERATE_AUTH_TOKEN,
  GENERATE_VIDEO_CREATOR_URL,
  USE_ATLAS_BACKEND,
} from '../config';
import {
  GenerateAdError,
  mapErrorTypeToMessage,
  type GenerateAdErrorType,
} from './generateAdRequest';

const CREATOR_TIMEOUT_MS = 300_000;
// Tope del poll post-202: tiene que cubrir el tope del provider de Seedance 2.5
// (~30 min desde 2026-08-31 — la cola GPU de Enhancor puede demorar incluso un
// video corto) más el wait inicial de ~4,7 min.
const CREATOR_PENDING_POLL_MS = 30 * 60 * 1000;

export interface VideoCreatorResult {
  /** Path relativo a `video-project-assets` (éxito normal). */
  path: string | null;
  /** Id de fila en `imagenes_generadas` (solo formato viejo de n8n). */
  videoId: string | null;
  /** true = atlas respondió 202 (wait interno de 280s vencido): el video
   *  SIGUE generándose. El caller decide (poll por execution_id, o mensaje
   *  de "tardó demasiado"). */
  pending: boolean;
  /** El video salió como BORRADOR (Profesional 480p): con `jobId` se lo pasa a 1080p hasta
   *  `venceEl` (ADR-2026-09-27-borrador-480p-y-pasar-a-1080p). `null` si no es borrador. */
  borrador: BorradorDelCreator | null;
}

export interface BorradorDelCreator {
  jobId: string;
  venceEl: string;
  segundos: number;
}

/** `output.borrador` del backend, sin el draft_id (nunca viaja al front). */
export function borradorDeLaRespuesta(out: unknown): BorradorDelCreator | null {
  const b = (out as { borrador?: Record<string, unknown> } | null)?.borrador;
  if (!b || typeof b.job_id !== 'string' || typeof b.vence_el !== 'string') return null;
  return { jobId: b.job_id, venceEl: b.vence_el, segundos: typeof b.segundos === 'number' ? b.segundos : 0 };
}

export async function generateVideoCreator(
  body: Record<string, unknown>,
): Promise<VideoCreatorResult> {
  const useAtlas =
    USE_ATLAS_BACKEND && !N8N_ONLY_REF_MODES.has(String(body.ref_mode ?? ''));

  let res: Response;
  try {
    if (useAtlas) {
      const { user_id: _u, company_id: _c, ...atlasBody } = body;
      void _u;
      void _c;
      res = await atlasBackendFetch('/v1/videos/creator', atlasBody, {
        signal: AbortSignal.timeout(CREATOR_TIMEOUT_MS),
      });
    } else {
      res = await fetch(GENERATE_VIDEO_CREATOR_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: GENERATE_AUTH_TOKEN,
        },
        body: JSON.stringify(body),
        signal: AbortSignal.timeout(CREATOR_TIMEOUT_MS),
      });
    }
  } catch (e: any) {
    if (e?.name === 'TimeoutError' || e?.name === 'AbortError') {
      throw new Error('La generación de video tardó demasiado. Probá de nuevo.');
    }
    throw new Error('No se pudo conectar. Revisá tu conexión.');
  }

  if (useAtlas && res.status === 202) {
    // El wait del backend venció pero el job SIGUE corriendo: antes acá se
    // devolvía pending y el flujo quedaba ciego (el creator no escribe filas en
    // imagenes_generadas — si el job fallaba después, el usuario no se enteraba
    // nunca). Ahora se pollea el status del job hasta que termine.
    const pendingBody: any = await res.json().catch(() => null);
    const jobId = pendingBody?.job_id ? String(pendingBody.job_id) : null;
    if (jobId) {
      const settled = await pollJobUntilSettled(jobId, {
        timeoutMs: CREATOR_PENDING_POLL_MS,
      });
      if (settled?.status === 'succeeded') {
        const out: any = settled.output ?? {};
        if (typeof out.path === 'string' && out.path) {
          return {
            path: out.path,
            videoId: out.video_id != null ? String(out.video_id) : null,
            pending: false,
            borrador: borradorDeLaRespuesta(out),
          };
        }
      }
      if (settled?.status === 'failed') {
        const t = (settled.errorType as GenerateAdErrorType) || 'technical';
        throw new GenerateAdError(
          t,
          settled.userMessage ?? mapErrorTypeToMessage(t),
          settled.errorType,
        );
      }
    }
    return { path: null, videoId: null, pending: true, borrador: null };
  }

  // n8n a veces responde 200 con cuerpo vacío o no-JSON cuando el proveedor
  // falló (ej. imagen de referencia que no se pudo procesar) — texto primero
  // para dar un error claro en vez del críptico "Unexpected end of JSON".
  const rawText = await res.text().catch(() => '');
  let json: any = null;
  if (rawText.trim()) {
    try {
      json = JSON.parse(rawText);
    } catch {
      throw new GenerateAdError(
        'technical',
        'El servidor de generación devolvió una respuesta inválida. Reintentá en un momento.',
        'invalid_json',
      );
    }
  }

  if (res.status === 402) {
    throw new GenerateAdError(
      'billing',
      mapErrorTypeToMessage('billing'),
      json?.error_message ?? 'billing',
    );
  }
  if (!res.ok) {
    // Atlas manda error_type tipado (censura/timeout/técnico) + mensaje ya
    // mostrable — respetarlo para que isActionable funcione (n8n: sin tipo).
    const t = (json?.error_type as GenerateAdErrorType) || 'technical';
    throw new GenerateAdError(
      t,
      json?.error_message || json?.error || mapErrorTypeToMessage(t),
      `HTTP ${res.status}`,
    );
  }
  if (json == null) {
    throw new GenerateAdError(
      'technical',
      'El servidor de generación no devolvió respuesta. Probablemente falló el procesamiento (imagen de referencia o parámetros). Reintentá o probá con otra imagen.',
      'empty_body',
    );
  }
  if (json?.response !== 'success' || (!json?.path && !json?.video_id)) {
    throw new GenerateAdError(
      'technical',
      json?.error_message || json?.error || 'La generación de video falló.',
      'unexpected_response',
    );
  }
  return {
    path: (json.path as string) ?? null,
    videoId: json.video_id != null ? String(json.video_id) : null,
    pending: false,
    borrador: borradorDeLaRespuesta(json),
  };
}

/** Lo que devuelve el escritor suelto: el prompt que escribiría el Creador para ese pedido. */
export interface PromptEscritoDeVideo {
  prompt: string;
  /** Slug del tipo que usó, o null si escribió libre. */
  skill_usada: string | null;
  modelo: string | null;
}

const ESCRIBIR_PROMPT_TIMEOUT_MS = 130_000;

/**
 * El escritor del Creador de video, suelto (`POST /v1/videos/creator/escribir-prompt`):
 * el MISMO que corre adentro de la generación, pero sin generar ni cobrar. Lo usa el botón
 * "Mejorar prompt" de Redes (ADR-2026-10-04-mejorar-prompt-en-redes). El body es el
 * subconjunto del del Creador que el escritor mira: `prompt`, `tipo_video`, `duration`,
 * `aspect_ratio`, `reference_images`, `variante_ids`, `first_frame`. Sólo existe en el
 * atlas-backend: sin el flag, no hay escritor que pedir.
 */
export async function escribirPromptDeVideo(body: Record<string, unknown>): Promise<PromptEscritoDeVideo> {
  if (!USE_ATLAS_BACKEND) throw new Error('Mejorar el prompt no está disponible en este entorno.');
  let res: Response;
  try {
    res = await atlasBackendFetch('/v1/videos/creator/escribir-prompt', body, {
      signal: AbortSignal.timeout(ESCRIBIR_PROMPT_TIMEOUT_MS),
    });
  } catch (e: any) {
    if (e?.name === 'TimeoutError' || e?.name === 'AbortError') {
      throw new Error('Mejorar el prompt tardó demasiado. Probá de nuevo.');
    }
    throw new Error('No se pudo conectar. Revisá tu conexión.');
  }
  const data: any = await res.json().catch(() => null);
  if (!res.ok || typeof data?.prompt !== 'string' || !data.prompt.trim()) {
    throw new Error(
      (typeof data?.error === 'string' && data.error) || 'No pudimos mejorar el prompt. Probá de nuevo.',
    );
  }
  return {
    prompt: data.prompt,
    skill_usada: typeof data.skill_usada === 'string' ? data.skill_usada : null,
    modelo: typeof data.modelo === 'string' ? data.modelo : null,
  };
}

/**
 * Cancela una escena del Estudio que se está generando, por su `execution_id` (el
 * `aiExecutionId` del clip) — ADR-2026-11-03-cancelar-una-escena-del-estudio. No cobra si
 * el video todavía no se generó; si ya se generó, `estado: "terminando"` y no se cancela.
 */
export async function cancelarEscena(
  executionId: string,
): Promise<{ cancelada: boolean; estado: string }> {
  try {
    const res = await atlasBackendFetch('/v1/videos/creator/cancelar', { execution_id: executionId }, {
      signal: AbortSignal.timeout(15_000),
    });
    const json = (await res.json().catch(() => null)) as { cancelada?: boolean; estado?: string } | null;
    if (!res.ok || !json) return { cancelada: false, estado: 'error' };
    return { cancelada: !!json.cancelada, estado: String(json.estado ?? '') };
  } catch {
    return { cancelada: false, estado: 'error' };
  }
}
